import type { OldTownFacadeTreatment } from "./oldTownBuildingArt.ts";

export type TownHallSurroundingFacadeTreatment = OldTownFacadeTreatment;

export interface TownHallSurroundingReference {
  readonly treatment: TownHallSurroundingFacadeTreatment;
  /** Official Registrų centras floor count already present in the M1 fixture. */
  readonly sourceFloorCount?: number;
  readonly palette?: Readonly<{
    id: string;
    wall: string;
    trim: string;
    accent: string;
  }>;
}

/**
 * The supplied Didžioji g. reference viewpoint transformed from WGS84
 * (54.6790209, 25.2875094) to the documented runtime origin.
 *
 * The reference images are used only to author geometry and colour families;
 * no photograph pixels enter the game.
 */
export const TOWN_HALL_SURROUNDINGS_REFERENCE_POINT = Object.freeze({
  x: 630.55,
  z: 1032.33,
});

export const TOWN_HALL_SURROUNDINGS_FLOOR_HEIGHT_M = 3.2;
export const TOWN_HALL_SURROUNDINGS_ROOF_ALLOWANCE_M = 2.2;

/**
 * Official GRPK footprints visible to the west and east from the supplied
 * Rotušės aikštė / Didžioji g. reference viewpoint.
 */
const TOWN_HALL_SURROUNDING_REFERENCES: ReadonlyMap<
  string,
  TownHallSurroundingReference
> = new Map([
  [
    "grpk:171560ab-3532-4f96-88eb-2b62b1ca6f69",
    { treatment: "pastel_dormered", sourceFloorCount: 2 },
  ],
  [
    "grpk:9b4e8e9f-ca30-49a4-9203-2cd3bbc718d5",
    { treatment: "pastel_dormered", sourceFloorCount: 3 },
  ],
  [
    "grpk:9fbd41fc-dba3-4991-b691-e7afc58ef5dc",
    { treatment: "pastel_dormered", sourceFloorCount: 2 },
  ],
  [
    "grpk:022f660e-b8f0-47f2-9b03-f2265da0ef9a",
    { treatment: "pastel_dormered", sourceFloorCount: 3 },
  ],
  [
    "grpk:cb4244d0-1366-4dee-9b8b-031bde22d719",
    { treatment: "pastel_dormered" },
  ],
  [
    "grpk:eff93f0b-216f-475e-a22b-49de7f8a8791",
    { treatment: "pastel_dormered", sourceFloorCount: 3 },
  ],
  [
    "grpk:ff809e6a-99a5-491c-b797-4467682cb697",
    {
      treatment: "classical_arcade",
      sourceFloorCount: 4,
      palette: {
        id: "town_hall_reference_ochre",
        wall: "#aa8e67",
        trim: "#c6b89e",
        accent: "#705b4a",
      },
    },
  ],
  [
    "grpk:08590f62-ad77-45e4-918f-e3e33a9af37a",
    {
      treatment: "classical_arcade",
      sourceFloorCount: 3,
      palette: {
        id: "town_hall_reference_sage",
        wall: "#879389",
        trim: "#b8b8a8",
        accent: "#5d685f",
      },
    },
  ],
  [
    "grpk:071d6df4-4fb5-4634-99e6-c1fc0bb516c1",
    {
      treatment: "classical_arcade",
      sourceFloorCount: 3,
      palette: {
        id: "town_hall_reference_limestone",
        wall: "#a9a092",
        trim: "#c7bdab",
        accent: "#71675c",
      },
    },
  ],
  [
    "grpk:991b8cd9-6602-4571-9dcf-79b57b24d337",
    {
      treatment: "classical_arcade",
      sourceFloorCount: 2,
      palette: {
        id: "town_hall_reference_pale_ochre",
        wall: "#b19a70",
        trim: "#c9bea8",
        accent: "#766049",
      },
    },
  ],
  [
    "grpk:d71a876e-4de6-403a-abcf-dfa638adf40b",
    { treatment: "classical_arcade", sourceFloorCount: 4 },
  ],
]);

export function townHallSurroundingReferenceForBuilding(
  buildingId: string,
): TownHallSurroundingReference | null {
  return TOWN_HALL_SURROUNDING_REFERENCES.get(buildingId) ?? null;
}

export const TOWN_HALL_SURROUNDING_BUILDING_IDS: ReadonlySet<string> =
  new Set(TOWN_HALL_SURROUNDING_REFERENCES.keys());
